import React, { useState } from 'react';
import { Stack, Button } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GetRecurringTasksForUnitDocument,
  GetRecurringTasksForUnitQuery,
  GetRecurringTasksForUnitQueryVariables,
  CreateRecurringTaskDocument,
  CreateRecurringTaskMutation,
  CreateRecurringTaskMutationVariables,
  UpdateRecurringTaskDocument,
  UpdateRecurringTaskMutation,
  UpdateRecurringTaskMutationVariables,
  RecurringTaskFragmentFragment,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { AddIcon } from '../../../components/icons';
import { NewRecurringTaskDialog } from '../../../components/new-recurring-task-dialog';
import { RecurringTasksList } from '../../../components/recurring-tasks-list';

interface UnitRecurringTasksSectionProps {
  unitId: string;
  disabled?: boolean;
}

export function UnitRecurringTasksSection({ unitId, disabled }: UnitRecurringTasksSectionProps) {
  const { show } = useAlert();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<RecurringTaskFragmentFragment | null>(null);

  const { data, refetch, loading, error } = useQuery<
    GetRecurringTasksForUnitQuery,
    GetRecurringTasksForUnitQueryVariables
  >(GetRecurringTasksForUnitDocument, {
    variables: { unitId },
  });

  const [createRecurringTask, { loading: createLoading }] = useMutation<
    CreateRecurringTaskMutation,
    CreateRecurringTaskMutationVariables
  >(CreateRecurringTaskDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť pravidelnú úlohu', e.message),
  });

  const [updateRecurringTask, { loading: updateLoading }] = useMutation<
    UpdateRecurringTaskMutation,
    UpdateRecurringTaskMutationVariables
  >(UpdateRecurringTaskDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa upraviť pravidelnú úlohu', e.message),
  });

  if (error) {
    return <div>Nepodarilo sa načítať pravidelné úlohy: {error.message}</div>;
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-start">
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)} disabled={disabled}>
          Pridať pravidelnú úlohu
        </Button>
      </Stack>

      <RecurringTasksList
        tasks={data?.getRecurringTasksForUnit || []}
        onRefresh={() => refetch()}
        onEdit={(task) => setEditingTask(task)}
        disabled={disabled}
      />

      {showAddDialog && (
        <NewRecurringTaskDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          unitId={unitId}
          onSubmit={(input) => {
            createRecurringTask({
              variables: { input: { ...input, unitId: unitId } },
              onCompleted: () => {
                setShowAddDialog(false);
                refetch();
              },
            });
          }}
          loading={createLoading}
        />
      )}

      {editingTask && (
        <NewRecurringTaskDialog
          open={true}
          editTask={editingTask}
          unitId={unitId}
          onClose={() => setEditingTask(null)}
          onSubmit={(input) => {
            updateRecurringTask({
              variables: { recurringTaskId: editingTask.id, input },
              onCompleted: () => {
                setEditingTask(null);
                refetch();
              },
            });
          }}
          loading={updateLoading}
        />
      )}
    </Stack>
  );
}
